import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDraft } from '../../state/draft';
import { STARTER_PROMPTS } from '@/webapp/plan/scaffolds';
import { funnelStep, useFunnelStep, type FunnelProps } from '../../analytics/funnel';
import { tailorPlan } from '../../state/tailor';
import { ApiError } from '../../api/client';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { Button, Screen } from '../../ui';
import { useSession } from '../../state/useSession';
import { demoForVisit, SITE_ORIGIN, type AdDemo } from '../../journey/demos';
import { currentVisit, secondsBucket } from '../../analytics/visits';
import { readIntoDraft } from '../../journey/attach';
import { CREATE_ACCEPT } from './createPicks';
import { useThumbnails } from './thumbnails';
import { stashDraft } from '../../state/draftStash';
import SignUp from '../auth/SignUp';

/** Long enough for any real sentence; short enough that a link can't paste in an essay. */
const MAX_LINKED_IDEA = 1000;

/**
 * The front door, ported from src/app/(onboarding)/create.tsx. One idea in, nothing else.
 *
 * The starters are deliberately specific — "an event app" produces mush, "RSVP for a
 * birthday dinner" produces an app — so tapping one goes straight to a plan.
 */
export default function Idea() {
  const navigate = useNavigate();
  const setDescription = useDraft((s) => s.setDescription);
  const setAppPlan = useDraft((s) => s.setPlan);
  const stored = useDraft((s) => s.description);
  // saddle-sites.com's hero links here with the sentence the visitor already typed. It
  // only fills the field — nothing is planned or spent until they press Continue.
  const [params] = useSearchParams();
  /*
   * Or it arrives in router state: the journey needs an account, so an ad's visitor signs up
   * first, and the sign-in that brings them back hands the idea over there rather than in the
   * address, where the by-then-live pixel would read it (shell/returnTo.ts).
   */
  const location = useLocation();
  const handedOver = (location.state as { idea?: unknown } | null)?.idea;
  const fromLink = (params.get('idea') ?? (typeof handedOver === 'string' ? handedOver : undefined))
    ?.trim()
    .slice(0, MAX_LINKED_IDEA);
  const [idea, setIdea] = useState(fromLink || stored);
  /*
   * Whether they came with their idea already written — an ad, or the landing site's hero. Read
   * once: the effect below takes the sentence out of the address, and the screen must not change
   * its mind about who it is talking to when it does.
   */
  const [linked] = useState(() => Boolean(fromLink));
  useFunnelStep('idea_viewed');


  /*
   * Read once, then taken out of the address bar.
   *
   * The sentence is the person's own words — an address, a child's name — and while it sits
   * in the URL, every PageView the Meta pixel sends carries the full URL with it. The privacy
   * policy says Meta is never sent prompts. Only `idea` goes: `fbclid` and `utm_*` are the
   * ad attribution the pixel exists for, and they stay. The field already holds the text.
   * ConsentBanner also waits for this param to be gone, so neither side relies on the order
   * the two effects run in.
   *
   * The idea handed over in router state is taken out of the history entry the same way. Left
   * there, the browser's Back from the plan put the ad's sentence back in the field over the
   * person's edit, and Continue from there threw the edit away (review, 2026-09-29). The field
   * holds the text; the draft holds it once they continue.
   */
  useEffect(() => {
    if (!params.has('idea') && typeof handedOver !== 'string') return;
    const next = new URLSearchParams(params);
    next.delete('idea');
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: null },
    );
    // Once, on arrival: the link is read a single time and the field owns the text after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The wait for the plan, shown here (2026-10-04). "Plan my app" used to jump to the plan screen at
   * once and let the tailored plan arrive some seconds later with nothing saying it was coming; ad
   * visitors spent 10 s to 2 min on this screen and left. Now the press starts the plan HERE, the
   * idea stays on screen, and each line under it is true when it shows: the idea was sent, the model
   * is reading it, and then what it actually planned. The plan screen opens with the plan ready
   * (state/tailor.ts shares the one request), or — when it fails — with its usual line about why.
   */
  const [waiting, setWaiting] = useState<{ startedAt: number; slow: boolean; plan: AppPlan | null; failed?: boolean } | null>(null);
  const inFlight = useRef<number | null>(null);
  useEffect(() => {
    const onHide = (): void => {
      if (inFlight.current === null) return;
      funnelStep('plan_abandoned', { seconds: planSeconds(Date.now() - inFlight.current) }, { now: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const { isAuthenticated } = useSession();
  /*
   * The ad's demo, live, before anything else (2026-10-04, preview-first journey step 1): an ad
   * visitor sees the app the ad promised, made with Saddle, and chooses to make their own. Anyone
   * without a matching demo (the landing site, a direct visit, an unknown ad) starts at the composer.
   */
  const [demo] = useState(() => (linked ? demoForVisit(currentVisit().utm_content, fromLink) : null));
  const [stage, setStage] = useState<'preview' | 'compose' | 'creating'>(() => (demo ? 'preview' : 'compose'));
  const prefilledWith = useRef<string>(fromLink ?? '');
  const attachedMedia = useDraft((s) => s.attachedMedia);
  const photos = useMemo(() => attachedMedia.filter((m) => m.contentType === 'image/jpeg'), [attachedMedia]);
  const thumbUrls = useThumbnails(photos);
  const thumbs = photos.map((p) => ({ name: p.name, url: thumbUrls[p.name] })).filter((t): t is { name: string; url: string } => Boolean(t.url));
  const [attachProblems, setAttachProblems] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const opened = useRef(Date.now());
  const typed = useRef(false);

  function chooseFromPreview(choice: 'make_own' | 'scratch'): void {
    funnelStep('preview_choice', { choice, seconds: secondsBucket(Date.now() - opened.current), loaded: demoLoaded() });
    if (choice === 'scratch') {
      setIdea('');
      prefilledWith.current = '';
    }
    opened.current = Date.now();
    setStage('compose');
  }
  // Leaving from the preview is a choice too: the ad's app was not enough.
  useEffect(() => {
    if (stage !== 'preview') return;
    const onHide = (): void => {
      funnelStep('preview_choice', { choice: 'left', seconds: secondsBucket(Date.now() - opened.current), loaded: demoLoaded() }, { now: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [stage]);

  function onType(next: string): void {
    if (!typed.current) {
      typed.current = true;
      const s = (Date.now() - opened.current) / 1000;
      funnelStep('composer_typing_started', {
        prefilled: prefilledWith.current.length > 0,
        seconds_to_first_key: s < 3 ? '<3' : s < 10 ? '3-10' : s < 30 ? '10-30' : '30+',
      });
    }
    setIdea(next);
  }

  async function attach(files: File[]): Promise<void> {
    if (!files.length) return;
    setAttaching(true);
    setAttachProblems([]);
    try {
      const { problems, addedPhotos, docs } = await readIntoDraft(files);
      setAttachProblems(problems);
      if (addedPhotos.length) funnelStep('composer_attach', { kind: 'photo', count: countBucket(addedPhotos.length) });
      if (docs) funnelStep('composer_attach', { kind: 'document', count: countBucket(docs) });
    } finally {
      setAttaching(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const go = (text: string): void => {
    const goal = text.trim();
    if (!goal || waiting) return;
    const was = prefilledWith.current.trim();
    funnelStep('create_pressed', {
      origin: !was ? 'typed' : goal === was ? 'prefilled_unchanged' : 'prefilled_edited',
      length: goal.length < 40 ? '<40' : goal.length < 120 ? '40-120' : goal.length < 300 ? '120-300' : '300+',
    });
    setDescription(goal);
    // Cleared so the plan screen always starts from a scaffold built for THIS idea,
    // never a plan left over from a previous run.
    setAppPlan(null);
    const startedAt = Date.now();
    inFlight.current = startedAt;
    funnelStep('plan_requested', { auto: false });
    setWaiting({ startedAt, slow: false, plan: null });
    /*
     * Signed out, the account is asked for NOW, over the loading view, so the plan's wait happens
     * while they type their email. The draft (idea and attachments) is put away first: the code
     * arrives by email, and a phone can drop the page on the way back.
     */
    if (!isAuthenticated) {
      setStage('creating');
      void stashDraft('').catch(() => undefined);
    }
    const slowLine = setTimeout(() => setWaiting((w) => (w && !w.plan ? { ...w, slow: true } : w)), 8000);
    tailorPlan(goal).then(
      (result) => {
        clearTimeout(slowLine);
        inFlight.current = null;
        funnelStep('plan_ready', { seconds: planSeconds(Date.now() - startedAt) });
        setWaiting((w) => (w ? { ...w, plan: result?.plan ?? null } : w));
        // Signed in: long enough to read what was planned, then the plan. Signed out: the card stays.
        if (signedInNow.current) setTimeout(() => navigate('/new/plan'), result?.plan ? 1200 : 0);
      },
      (err: unknown) => {
        clearTimeout(slowLine);
        inFlight.current = null;
        funnelStep('plan_failed', { reason: failureReason(err) });
        setWaiting((w) => (w ? { ...w, failed: true } : w));
        if (signedInNow.current) navigate('/new/plan');
      },
    );
  };
  // Read when the plan lands, not when it was asked for: they may have signed in meanwhile.
  const signedInNow = useRef(isAuthenticated);
  signedInNow.current = isAuthenticated;

  if (stage === 'preview' && demo) {
    return <DemoPreview demo={demo} onChoose={chooseFromPreview} />;
  }

  if (stage === 'creating') {
    return (
      <Screen>
        <CreatingView idea={idea} thumbs={thumbs} waiting={waiting} />
        <SignUp card={{ from: '/new/plan' }} />
      </Screen>
    );
  }

  return (
    <Screen title="What do you want to make?">
      <p className="type-body idea-sub">
        {linked ? 'Change anything you like, add your photos, then tap Create.' : 'Describe it in a line. Add photos or a menu with +.'}
      </p>
      <div className="composer">
        {thumbs.length > 0 && (
          <ul className="composer-thumbs" aria-label="Attached photos">
            {thumbs.map((t) => <li key={t.name}><img src={t.url} alt="" /></li>)}
          </ul>
        )}
        <label className="visually-hidden" htmlFor="idea">Describe the app you want</label>
        <textarea
          id="idea"
          className="field idea-input composer-input"
          value={idea}
          rows={4}
          readOnly={waiting !== null}
          onChange={(e) => onType(e.target.value)}
          placeholder="Describe what you want to make…"
        />
        <div className="composer-bar">
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={CREATE_ACCEPT}
            className="visually-hidden"
            aria-label="Photos or documents to add"
            onChange={(e) => void attach(Array.from(e.target.files ?? []))}
          />
          <button
            type="button"
            className="composer-plus"
            aria-label="Add photos or documents"
            disabled={attaching || waiting !== null}
            onClick={() => fileInput.current?.click()}
          >
            +
          </button>
          <span className="type-caption composer-hint">
            {attaching ? 'Reading your files…' : attachedMedia.length ? `${attachedMedia.length} photo${attachedMedia.length === 1 ? '' : 's'} added` : 'Photos, a menu, a schedule'}
          </span>
        </div>
      </div>
      {attachProblems.length > 0 && (
        <div role="alert" className="type-caption field-error-text">
          {attachProblems.map((p) => <p key={p}>{p}</p>)}
        </div>
      )}
      <Button
        title={waiting ? 'Planning your app…' : 'Create'}
        loading={waiting !== null && !waiting.plan}
        onPress={() => go(idea)}
        disabled={idea.trim().length === 0 || waiting !== null || attaching}
      />
      {waiting && <PlanProgress waiting={waiting} />}

      {/* Somebody who arrived with their own idea is not offered eight others. */}
      {!linked && !waiting && (
        <>
          <h2 className="type-micro section-label">Or start from one of these</h2>
          <div className="starter-grid">
            {STARTER_PROMPTS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="card clay-card pressable starter"
                aria-label={`${s.label}. ${s.prompt}`}
                onClick={() => go(s.prompt)}
              >
                <span className="type-heading">{s.label}</span>
                <span className="type-caption starter-sub">{s.prompt}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </Screen>
  );
}

/** The wait as the plan's own buckets (analytics/funnel.ts). */
function planSeconds(ms: number): NonNullable<FunnelProps['seconds']> {
  const s = ms / 1000;
  return s < 5 ? '<5' : s < 10 ? '5-10' : s < 20 ? '10-20' : '20+';
}

/** Why a plan did not come: the day's free plans, the clock, or anything else. */
function failureReason(err: unknown): NonNullable<FunnelProps['reason']> {
  if (err instanceof ApiError) {
    if (err.code === 'anon_plan_limit' || err.code === 'subscription_required' || err.kind === 'out_of_credits' || err.kind === 'rate_limited') return 'cap';
    if (err.kind === 'timeout') return 'timeout';
  }
  return 'error';
}

/**
 * What is happening, in lines that are each true when they appear: the idea went, the model is
 * reading it (with the honest "about 10 seconds" once it has taken a while), and then what it planned.
 */
function PlanProgress({ waiting }: { waiting: { slow: boolean; plan: AppPlan | null } }) {
  const included = waiting.plan?.features.filter((f) => f.included) ?? [];
  return (
    <ul className="plan-progress" role="status" aria-live="polite">
      <li className="plan-progress-done">Got your idea</li>
      {waiting.plan ? (
        <>
          <li className="plan-progress-done">Read it and chose what your app needs</li>
          <li className="plan-progress-done">
            Planned {included.length} feature{included.length === 1 ? '' : 's'}
            {included.length ? `: ${included.slice(0, 3).map((f) => f.label).join(', ')}${included.length > 3 ? '…' : ''}` : ''}
          </li>
          <li className="plan-progress-now">Opening your plan…</li>
        </>
      ) : (
        <>
          <li className="plan-progress-now">Reading it and choosing the pages and features…</li>
          {waiting.slow && <li className="plan-progress-note">Plans usually take about 10 seconds.</li>}
        </>
      )}
    </ul>
  );
}

function countBucket(n: number): NonNullable<FunnelProps['count']> {
  return n <= 1 ? '1' : n <= 5 ? '2-5' : '6+';
}

/**
 * The ad's app, live, in a phone frame (step 1). Captioned with the sentence it was made from, and
 * one action: make your own. "Start from scratch" is there, quietly, for anyone with another idea.
 * The demo is only SHOWN: framed read-only from its own site, never copied, changed or republished.
 */
/** Whether the demo frame on screen has loaded (read when the choice is made). */
function demoLoaded(): boolean {
  return document.querySelector('.demo-phone-loaded') !== null;
}

function DemoPreview({ demo, onChoose }: { demo: AdDemo; onChoose: (c: 'make_own' | 'scratch') => void }) {
  useFunnelStep('preview_viewed', true, { demo: demo.slug, ref: currentVisit().ref });
  /*
   * How long the live demo took to appear (handoff follow-up, 2026-10-05): 10 of the first 15
   * visitors inside Facebook's browser stopped on this screen, and a slow frame there looks exactly
   * like a lack of interest. `preview_loaded` says how long; the choice says whether it had loaded.
   */
  const opened = useRef(Date.now());
  const [loaded, setLoaded] = useState(false);
  const onLoad = (): void => {
    if (loaded) return;
    setLoaded(true);
    funnelStep('preview_loaded', { demo: demo.slug, seconds: secondsBucket(Date.now() - opened.current) });
  };
  return (
    <Screen>
      <div className="demo-preview">
        <p className="type-caption demo-caption">
          Made with Saddle from: <q>{demo.sentence}</q>
        </p>
        <div className={loaded ? 'demo-phone demo-phone-loaded' : 'demo-phone'}>
          {/* Until the live app arrives: a still frame, so the screen never reads as broken. */}
          {!loaded && (
            <div className="demo-loading" role="status">
              <span className="demo-loading-bar" /><span className="demo-loading-bar" /><span className="demo-loading-bar demo-loading-short" />
              <span className="visually-hidden">Loading the live app…</span>
            </div>
          )}
          <iframe
            title="The app this ad shows, live"
            src={`${SITE_ORIGIN}/site/${demo.slug}`}
            loading="eager"
            referrerPolicy="no-referrer"
            onLoad={onLoad}
          />
        </div>
        {/*
          Pinned to the bottom of the screen from the first paint — before the demo has loaded, and on
          a phone whose browser bars leave less room than the design assumed — so the one action this
          screen asks for is never below the fold.
        */}
        <div className="demo-actions">
          <Button title="Make my own" onPress={() => onChoose('make_own')} />
          <button type="button" className="link-button demo-scratch" onClick={() => onChoose('scratch')}>
            Start from scratch
          </button>
        </div>
      </div>
    </Screen>
  );
}

/**
 * The wait after Create, signed out (step 3): their idea quoted, their photos, a phone filling in,
 * and steps that are each true when they show. The sign-up card sits over it.
 */
function CreatingView({ idea, thumbs, waiting }: {
  idea: string;
  thumbs: { name: string; url: string }[];
  waiting: { slow: boolean; plan: AppPlan | null; failed?: boolean } | null;
}) {
  const included = waiting?.plan?.features.filter((f) => f.included) ?? [];
  const planned = Boolean(waiting?.plan) || Boolean(waiting?.failed);
  return (
    <div className="creating">
      <blockquote className="creating-idea">{idea}</blockquote>
      <div className={planned ? 'creating-phone creating-phone-ready' : 'creating-phone'} aria-hidden="true">
        <span /><span /><span /><span />
        {thumbs.slice(0, 3).map((t) => <img key={t.name} src={t.url} alt="" />)}
      </div>
      <ul className="plan-progress" role="status" aria-live="polite">
        <li className="plan-progress-done">Got your idea</li>
        {thumbs.length > 0 && <li className="plan-progress-done">{thumbs.length} photo{thumbs.length === 1 ? '' : 's'} ready to place</li>}
        {waiting?.plan ? (
          <li className="plan-progress-done">
            Planned {included.length} feature{included.length === 1 ? '' : 's'}
            {included.length ? `: ${included.slice(0, 3).map((f) => f.label).join(', ')}${included.length > 3 ? '…' : ''}` : ''}
          </li>
        ) : waiting?.failed ? (
          <li className="plan-progress-done">Your plan is ready to finish once you're in</li>
        ) : (
          <li className="plan-progress-now">Reading your idea and choosing the pages…</li>
        )}
      </ul>
    </div>
  );
}
